/**
 * JavaScript / pixel tracking (ITP-compliant) — operator page (feature #162).
 *
 * Three regions:
 *
 *   1. Register-snippet form — site label + allowed origin + event-type
 *      pills. On submit, posts to `/api/v1/js-pixel/snippet` and surfaces
 *      the install snippet with a "Copy" button.
 *
 *   2. Registered-snippets table — one row per site. Each row shows status
 *      (enabled/disabled), allowed origin, tracked event types, and actions
 *      (toggle enabled, copy snippet, delete). Selecting a row loads that
 *      snippet's recent beacons below.
 *
 *   3. Recent-events list — the live view of beacons landed for the selected
 *      snippet. Highlights itp_fallback hits so operators can see where
 *      Safari's 7-day storage cap kicked in and the server re-attached the
 *      visitor via the coarse fingerprint.
 *
 * Brand: emerald → sky gradient headline, Inter, zinc borders, 6-8px rounded,
 * light theme only. No emojis, no competitor names.
 *
 * The file is intentionally monolithic — breaking sub-components into
 * sibling files would widen the owned-file surface. Internal widgets live
 * here as local function components.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	PIXEL_EVENT_TYPE_LABELS,
	PIXEL_EVENT_TYPES,
	buildInstallSnippet,
	copyToClipboard,
	countByEnabled,
	createSnippet,
	deleteSnippet,
	diffDraft,
	EMPTY_DRAFT,
	eventTypeBadgeClass,
	formatRelative,
	formatTimestamp,
	itpFallbackRate,
	jsPixelQueryKeys,
	listEvents,
	listSnippets,
	removeSnippet,
	snippetDraftSchema,
	sortSnippets,
	statusBadgeClass,
	tallyEventTypes,
	updateSnippet,
	upsertSnippet,
	type JsPixelSnippetView,
	type PixelEventType,
	type SnippetDraft,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens (brand rules)
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-300 bg-white px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const PILL_BASE =
	"inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium";

// ---------------------------------------------------------------------------
// Props — deep-link an initial snippet id via ?snippetId= so operators can
// jump directly to a previously-registered snippet.
// ---------------------------------------------------------------------------

export interface JsPixelPageProps {
	initialSnippetId?: string;
}

export function JsPixelPage({ initialSnippetId }: JsPixelPageProps) {
	const queryClient = useQueryClient();

	const [draft, setDraft] = useState<SnippetDraft>({ ...EMPTY_DRAFT });
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [lastCreated, setLastCreated] = useState<JsPixelSnippetView | null>(
		null,
	);
	const [selectedId, setSelectedId] = useState<string>(
		initialSnippetId ?? "",
	);

	const listQuery = useQuery({
		queryKey: jsPixelQueryKeys.list(),
		queryFn: () => listSnippets(),
	});

	const sorted = useMemo(
		() => (listQuery.data ? sortSnippets(listQuery.data) : []),
		[listQuery.data],
	);
	const totals = useMemo(() => countByEnabled(sorted), [sorted]);

	const eventsQuery = useQuery({
		queryKey: jsPixelQueryKeys.events(selectedId),
		queryFn: () => listEvents(selectedId, 50),
		enabled: selectedId.trim().length > 0,
	});

	const createMutation = useMutation({
		mutationFn: createSnippet,
		onSuccess: (row) => {
			toast.success(`Snippet for "${row.site_label}" created.`);
			setLastCreated(row);
			setDraft({ ...EMPTY_DRAFT });
			setDraftErrors({});
			setSelectedId(row.id);
			queryClient.setQueryData<JsPixelSnippetView[]>(
				jsPixelQueryKeys.list(),
				(prev) => (prev ? upsertSnippet(prev, row) : [row]),
			);
			queryClient.invalidateQueries({ queryKey: jsPixelQueryKeys.list() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to create snippet.");
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({
			id,
			patch,
		}: {
			id: string;
			patch: Parameters<typeof updateSnippet>[1];
		}) => updateSnippet(id, patch),
		onSuccess: (row) => {
			toast.success(
				`Snippet "${row.site_label}" ${row.enabled ? "enabled" : "paused"}.`,
			);
			queryClient.setQueryData<JsPixelSnippetView[]>(
				jsPixelQueryKeys.list(),
				(prev) => (prev ? upsertSnippet(prev, row) : [row]),
			);
			queryClient.invalidateQueries({ queryKey: jsPixelQueryKeys.list() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to update snippet.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteSnippet(id),
		onSuccess: (_void, id) => {
			toast.success("Snippet removed.");
			queryClient.setQueryData<JsPixelSnippetView[]>(
				jsPixelQueryKeys.list(),
				(prev) => (prev ? removeSnippet(prev, id) : prev),
			);
			if (selectedId === id) setSelectedId("");
			queryClient.invalidateQueries({ queryKey: jsPixelQueryKeys.list() });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove snippet.");
		},
	});

	function toggleEventType(t: PixelEventType) {
		setDraft((prev) => {
			const has = prev.eventTypes.includes(t);
			const next = has
				? prev.eventTypes.filter((x) => x !== t)
				: [...prev.eventTypes, t];
			return { ...prev, eventTypes: next };
		});
	}

	function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
		e.preventDefault();
		const parsed = snippetDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data);
	}

	async function handleCopy(row: JsPixelSnippetView) {
		const text = buildInstallSnippet(row);
		const ok = await copyToClipboard(text);
		if (ok) toast.success("Install snippet copied to clipboard.");
		else toast.error("Clipboard copy failed — select and copy manually.");
	}

	function handleToggleEnabled(row: JsPixelSnippetView) {
		updateMutation.mutate({
			id: row.id,
			patch: { enabled: !row.enabled },
		});
	}

	function handleDelete(row: JsPixelSnippetView) {
		if (
			typeof window !== "undefined" &&
			!window.confirm(
				`Remove the pixel for ${row.site_label}? Installed snippets will stop firing.`,
			)
		) {
			return;
		}
		deleteMutation.mutate(row.id);
	}

	const now = Math.floor(Date.now() / 1000);
	const sortedEvents = eventsQuery.data ?? [];
	const eventTally = useMemo(
		() => tallyEventTypes(sortedEvents),
		[sortedEvents],
	);
	const fbRate = useMemo(
		() => itpFallbackRate(sortedEvents),
		[sortedEvents],
	);

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-6 pb-16 pt-6">
			<header className="flex flex-col gap-2">
				<h1 className="font-inter bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-2xl font-semibold tracking-tight text-transparent">
					JavaScript pixel
				</h1>
				<p className="max-w-3xl text-sm text-zinc-600">
					Register a JavaScript snippet for a site. The browser writes a
					first-party cookie (capped at Safari's 7-day storage ceiling) and
					refreshes it on every beacon. When the cookie ages out, the server
					re-attaches the visitor via a coarse UA/IP fingerprint so operators
					keep a continuous session view.
				</p>
			</header>

			<section aria-labelledby="register-heading" className={`${CARD} p-6`}>
				<h2
					id="register-heading"
					className="text-base font-semibold text-zinc-900"
				>
					Register a pixel
				</h2>
				<form
					onSubmit={handleSubmit}
					className="mt-4 flex flex-col gap-4 md:flex-row md:items-start md:gap-4"
				>
					<div className="flex w-full flex-col gap-1 md:max-w-xs">
						<label htmlFor="siteLabel" className={LABEL}>
							Site label
						</label>
						<input
							id="siteLabel"
							name="siteLabel"
							type="text"
							className={INPUT}
							placeholder="Marketing site"
							value={draft.siteLabel}
							onChange={(e) =>
								setDraft((prev) => ({ ...prev, siteLabel: e.target.value }))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
						/>
						{draftErrors.siteLabel ? (
							<span className={ERROR_TEXT}>{draftErrors.siteLabel}</span>
						) : (
							<span className={HINT}>
								Shown only in this dashboard.
							</span>
						)}
					</div>
					<div className="flex w-full flex-col gap-1">
						<label htmlFor="allowedOrigin" className={LABEL}>
							Allowed origin
						</label>
						<input
							id="allowedOrigin"
							name="allowedOrigin"
							type="text"
							className={INPUT}
							placeholder="https://brand.com"
							value={draft.allowedOrigin}
							onChange={(e) =>
								setDraft((prev) => ({
									...prev,
									allowedOrigin: e.target.value,
								}))
							}
							disabled={createMutation.isPending}
							autoComplete="off"
							spellCheck={false}
						/>
						{draftErrors.allowedOrigin ? (
							<span className={ERROR_TEXT}>
								{draftErrors.allowedOrigin}
							</span>
						) : (
							<span className={HINT}>
								Full origin — scheme + host + optional port. Beacons from
								other origins are dropped.
							</span>
						)}
					</div>
					<div className="flex items-end">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMutation.isPending}
						>
							{createMutation.isPending ? "Creating…" : "Create pixel"}
						</button>
					</div>
				</form>

				<fieldset className="mt-5">
					<legend className={LABEL}>Event types</legend>
					<div className="mt-2 flex flex-wrap gap-2">
						{PIXEL_EVENT_TYPES.map((t) => {
							const active = draft.eventTypes.includes(t);
							return (
								<button
									key={t}
									type="button"
									onClick={() => toggleEventType(t)}
									className={`${PILL_BASE} ${
										active
											? "border-emerald-300 bg-emerald-50 text-emerald-700"
											: "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50"
									}`}
									aria-pressed={active}
								>
									{PIXEL_EVENT_TYPE_LABELS[t]}
								</button>
							);
						})}
					</div>
					{draftErrors.eventTypes ? (
						<span className={`${ERROR_TEXT} mt-2 block`}>
							{draftErrors.eventTypes}
						</span>
					) : (
						<span className={`${HINT} mt-2 block`}>
							Click a pill to toggle. Defaults cover page views, clicks, and
							conversions.
						</span>
					)}
				</fieldset>

				{lastCreated ? (
					<div className="mt-6 rounded-[6px] border border-emerald-200 bg-emerald-50 p-4">
						<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
							<h3 className="text-sm font-semibold text-emerald-900">
								Install snippet for {lastCreated.site_label}
							</h3>
							<button
								type="button"
								className={BTN_SECONDARY}
								onClick={() => handleCopy(lastCreated)}
							>
								Copy snippet
							</button>
						</div>
						<pre className="mt-3 overflow-x-auto rounded-[6px] border border-emerald-200 bg-white p-3 text-xs leading-relaxed text-zinc-900">
							<code>{buildInstallSnippet(lastCreated)}</code>
						</pre>
						<p className="mt-3 text-xs text-emerald-800">
							Paste into the site's &lt;head&gt;. The loader is cached for 5
							minutes so repeat page loads are cheap.
						</p>
					</div>
				) : null}
			</section>

			<section aria-labelledby="registered-heading" className={`${CARD} p-6`}>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<h2
						id="registered-heading"
						className="text-base font-semibold text-zinc-900"
					>
						Registered pixels
					</h2>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => listQuery.refetch()}
						disabled={listQuery.isFetching}
					>
						{listQuery.isFetching ? "Refreshing…" : "Refresh"}
					</button>
				</div>

				<div className="mt-4 grid grid-cols-2 gap-3 md:max-w-md">
					<StatCell label="Enabled" value={totals.enabled} />
					<StatCell label="Paused" value={totals.disabled} />
				</div>

				<div className="mt-6 overflow-hidden rounded-[6px] border border-zinc-200">
					<table className="w-full text-sm">
						<thead>
							<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
								<th className="px-4 py-2 font-medium">Site</th>
								<th className="px-4 py-2 font-medium">Status</th>
								<th className="px-4 py-2 font-medium">Origin</th>
								<th className="px-4 py-2 font-medium">Events</th>
								<th className="px-4 py-2 font-medium">Created</th>
								<th className="px-4 py-2 text-right font-medium">Actions</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{listQuery.isLoading ? (
								<tr>
									<td
										colSpan={6}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										Loading pixels…
									</td>
								</tr>
							) : listQuery.isError ? (
								<tr>
									<td
										colSpan={6}
										className="px-4 py-8 text-center text-sm text-red-600"
									>
										{(listQuery.error as Error | undefined)?.message ??
											"Unable to load pixels."}
									</td>
								</tr>
							) : sorted.length === 0 ? (
								<tr>
									<td
										colSpan={6}
										className="px-4 py-8 text-center text-sm text-zinc-500"
									>
										No pixels registered yet. Create one above.
									</td>
								</tr>
							) : (
								sorted.map((row) => (
									<tr
										key={row.id}
										className={
											row.id === selectedId
												? "bg-emerald-50/30"
												: "hover:bg-zinc-50"
										}
									>
										<td className="px-4 py-3 text-zinc-900">
											<div className="font-medium">{row.site_label}</div>
											<div className="text-xs text-zinc-500">{row.id}</div>
										</td>
										<td className="px-4 py-3">
											<span
												className={`${PILL_BASE} ${statusBadgeClass(row.enabled)}`}
											>
												{row.enabled ? "Enabled" : "Paused"}
											</span>
										</td>
										<td className="break-all px-4 py-3 font-mono text-xs text-zinc-700">
											{row.allowed_origin}
										</td>
										<td className="px-4 py-3">
											<div className="flex flex-wrap gap-1">
												{row.event_types.map((t) => (
													<span
														key={t}
														className={`${PILL_BASE} ${eventTypeBadgeClass(t)}`}
													>
														{PIXEL_EVENT_TYPE_LABELS[t]}
													</span>
												))}
											</div>
										</td>
										<td className="px-4 py-3 text-zinc-600">
											{formatTimestamp(row.created_at)}
										</td>
										<td className="px-4 py-3 text-right">
											<div className="inline-flex flex-wrap justify-end gap-2">
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() => setSelectedId(row.id)}
												>
													View events
												</button>
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() => handleCopy(row)}
												>
													Copy snippet
												</button>
												<button
													type="button"
													className={BTN_SECONDARY}
													onClick={() => handleToggleEnabled(row)}
													disabled={updateMutation.isPending}
												>
													{row.enabled ? "Pause" : "Enable"}
												</button>
												<button
													type="button"
													className={BTN_DANGER}
													onClick={() => handleDelete(row)}
													disabled={deleteMutation.isPending}
												>
													Remove
												</button>
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			</section>

			{selectedId ? (
				<section aria-labelledby="events-heading" className={`${CARD} p-6`}>
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
						<div className="flex flex-col gap-1">
							<h2
								id="events-heading"
								className="text-base font-semibold text-zinc-900"
							>
								Recent beacons
							</h2>
							<p className={HINT}>
								Most-recent 50 beacons for snippet{" "}
								<span className="font-mono">{selectedId}</span>. ITP fallback
								rate in this window:{" "}
								<span className="font-medium text-zinc-700">
									{(fbRate * 100).toFixed(1)}%
								</span>
								.
							</p>
						</div>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => eventsQuery.refetch()}
							disabled={eventsQuery.isFetching}
						>
							{eventsQuery.isFetching ? "Refreshing…" : "Refresh"}
						</button>
					</div>

					<div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 md:max-w-xl">
						<StatCell label="Page" value={eventTally.page} />
						<StatCell label="Click" value={eventTally.click} />
						<StatCell label="Conversion" value={eventTally.conv} />
						<StatCell label="Custom" value={eventTally.custom} />
					</div>

					<div className="mt-6 overflow-hidden rounded-[6px] border border-zinc-200">
						<table className="w-full text-sm">
							<thead>
								<tr className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
									<th className="px-4 py-2 font-medium">Type</th>
									<th className="px-4 py-2 font-medium">Visitor</th>
									<th className="px-4 py-2 font-medium">URL</th>
									<th className="px-4 py-2 font-medium">Seen</th>
									<th className="px-4 py-2 font-medium">ITP fallback</th>
								</tr>
							</thead>
							<tbody className="divide-y divide-zinc-100">
								{eventsQuery.isLoading ? (
									<tr>
										<td
											colSpan={5}
											className="px-4 py-8 text-center text-sm text-zinc-500"
										>
											Loading beacons…
										</td>
									</tr>
								) : eventsQuery.isError ? (
									<tr>
										<td
											colSpan={5}
											className="px-4 py-8 text-center text-sm text-red-600"
										>
											{(eventsQuery.error as Error | undefined)?.message ??
												"Unable to load beacons."}
										</td>
									</tr>
								) : sortedEvents.length === 0 ? (
									<tr>
										<td
											colSpan={5}
											className="px-4 py-8 text-center text-sm text-zinc-500"
										>
											No beacons for this pixel yet. Load the site or call{" "}
											<span className="font-mono">window.tr.track()</span>{" "}
											to emit one.
										</td>
									</tr>
								) : (
									sortedEvents.map((e) => (
										<tr key={e.id}>
											<td className="px-4 py-3">
												<span
													className={`${PILL_BASE} ${eventTypeBadgeClass(e.event_type)}`}
												>
													{PIXEL_EVENT_TYPE_LABELS[e.event_type]}
												</span>
											</td>
											<td className="break-all px-4 py-3 font-mono text-xs text-zinc-700">
												{e.visitor_id}
											</td>
											<td className="break-all px-4 py-3 font-mono text-xs text-zinc-600">
												{e.url ?? "—"}
											</td>
											<td className="px-4 py-3 text-zinc-600">
												<div>{formatRelative(e.created_at, now)}</div>
												<div className="text-xs text-zinc-400">
													{formatTimestamp(e.created_at)}
												</div>
											</td>
											<td className="px-4 py-3">
												{e.itp_fallback ? (
													<span
														className={`${PILL_BASE} border-amber-200 bg-amber-50 text-amber-700`}
													>
														Re-attached
													</span>
												) : (
													<span
														className={`${PILL_BASE} border-zinc-200 bg-zinc-50 text-zinc-500`}
													>
														Cookie
													</span>
												)}
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					</div>
				</section>
			) : null}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Local widgets
// ---------------------------------------------------------------------------

function StatCell({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-[6px] border border-zinc-200 bg-white px-3 py-2">
			<div className="text-xs font-medium text-zinc-500">{label}</div>
			<div className="text-lg font-semibold text-zinc-900">{value}</div>
		</div>
	);
}

// Diff helper re-exported for tests that exercise the page's patch logic.
export { diffDraft };
